import { findIndex, filter, find, get } from 'lodash'
import routes from '~/const/routes'

export const state = () => ({
  items: null,
  flatFoldersTree: [],
  currentFolder: null,
  meta: {
    totalItems: null
  },
  selectedFolder: null
})

export const getters = {
  items: state => state.items,
  meta: state => state.meta,
  flatFoldersTree: state => state.flatFoldersTree,
  currentFolder: state => state.currentFolder,
  selectedFolder: state => state.selectedFolder
}

// TODO: move out
const recursivelyFindFolder = (folder, id) => {
  if (folder.id === id) {
    return folder
  }
  if (!folder.children) {
    return null
  }

  for (const child of folder.children) {
    const found = recursivelyFindFolder(child, id)
    if (found) {
      return found
    }
  }
  return null
}

export const mutations = {
  SET_ITEMS(state, items) {
    state.items = items
  },
  SET_META(state, meta) {
    state.meta = meta
  },
  SET_CURRENT_FOLDER(state, folder) {
    state.currentFolder = folder
  },
  ADD_FOLDER(state, { folder, parentId = null, inModal = false }) {
    if (parentId) {
      const firstFolder = inModal ? state.selectedFolder : state.flatFoldersTree[0]
      const rootFolder = find(state.items, item => item.id === firstFolder.id)
      const parentFolder = inModal
        ? state.selectedFolder
        : recursivelyFindFolder(rootFolder, parentId)
      if (parentFolder.id === parentId) {
        parentFolder.children.unshift(folder)
      }
      if (state.currentFolder) {
        const shouldUpdateCurrentFolder = find(
          state.currentFolder.children,
          child => child.id === folder.id
        )
        if (parentFolder.id === state.currentFolder.id && !shouldUpdateCurrentFolder) {
          state.currentFolder.children.unshift(folder)
        }
      }
    } else {
      state.items.unshift(folder)
    }
  },
  UPDATE_FOLDER_NAME(state, { updatedFolder, parentId = null }) {
    if (parentId) {
      const firstFolder = state.flatFoldersTree[0]
      const rootFolder = find(state.items, item => item.id === firstFolder.id)
      const folder = recursivelyFindFolder(rootFolder, updatedFolder.id)
      if (folder.id === updatedFolder.id) {
        folder.name = updatedFolder.name
      }
    } else {
      const folder = find(state.items, item => item.id === updatedFolder.id)
      folder.name = updatedFolder.name
    }
  },
  UPDATE_FOLDER_PARENT_ID(state, { folderToPut, parentId }) {
    const firstFolder = state.flatFoldersTree[0]
    if (!firstFolder) {
      const folder = find(state.items, item => item.id === parentId)
      folder.children.unshift(folderToPut)
    } else {
      const rootFolder = find(state.items, item => item.id === firstFolder.id)
      const folder = recursivelyFindFolder(rootFolder, parentId)
      folder.children.unshift(folderToPut)
    }
  },
  REMOVE_FOLDER(state, { id, parentId = null }) {
    if (parentId) {
      const firstFolder = state.flatFoldersTree[0]
      const rootFolder = find(state.items, item => item.id === firstFolder.id)
      let parentFolder
      if (parentId === id) {
        // handle delete active folder (last folder in flatFoldersTree)
        const parentOfActiveFolder = state.flatFoldersTree[state.flatFoldersTree.length - 2]
        parentFolder = recursivelyFindFolder(rootFolder, parentOfActiveFolder?.id)
      } else {
        parentFolder = recursivelyFindFolder(rootFolder, parentId)
      }
      if (parentFolder) {
        parentFolder.children = filter(parentFolder.children, child => child.id !== id)
        if (parentId === id) {
          // delete breadcrumb only if deleting folder being inside it
          state.flatFoldersTree.splice(-1, 1)
        }
      }
    } else {
      state.items = filter(state.items, item => item.id !== id)
    }
  },
  ADD_FOLDER_TO_FLAT_LIST(state, folder) {
    state.flatFoldersTree.push(folder)
  },
  RESET_FLAT_LIST(state) {
    state.flatFoldersTree = []
  },
  SPLICE_FLAT_LIST(state, itemIdx) {
    state.flatFoldersTree.splice(itemIdx + 1)
  },
  RESET(state) {
    state.items = []
    state.meta = { totalItems: null }
    state.flatFoldersTree = []
    state.currentFolder = null
    state.selectedFolder = null
  },
  SET_SELECTED_FOLDER(state, folder) {
    state.selectedFolder = folder
  },
  RESET_SELECTED_FOLDER(state) {
    state.selectedFolder = null
  }
}

export const actions = {
  // CRUD operations
  async getAll({ commit }, { config = {} }) {
    const { data, meta } = get(await this.$axios.get(routes.folder.list(), config), 'data')

    commit('SET_ITEMS', data)
    commit('SET_META', meta)

    return { data, meta }
  },
  async create({ commit }, { payload, inModal = false, config = {} }) {
    const folder = get(await this.$axios.post(routes.folder.list(), payload, config), 'data')

    commit('ADD_FOLDER', {
      folder: { ...folder, children: [] },
      parentId: payload.parentId,
      inModal
    })

    return folder
  },
  async update({ state, commit }, { folder, payload, moveFolder = false, config = {} }) {
    const updatedFolder = get(
      await this.$axios.put(routes.folder.concrete(folder.id), payload, config),
      'data'
    )
    if (moveFolder) {
      // update folder place (move folder in folder)
      commit('UPDATE_FOLDER_PARENT_ID', {
        folderToPut: { ...updatedFolder, children: folder.children || [] },
        parentId: payload.parentId
      })
      commit('REMOVE_FOLDER', {
        id: folder.id,
        parentId: state.flatFoldersTree.length ? get(state.flatFoldersTree.at(-1), 'id') : null
      })
    } else {
      // update folder name
      commit('UPDATE_FOLDER_NAME', { updatedFolder, parentId: payload.parentId })
    }
  },
  async delete({ commit, getters }, { id, parentId, config = {} }) {
    await this.$axios.delete(routes.folder.concrete(id), config)
    commit('REMOVE_FOLDER', { id, parentId })
    if (id === parentId) {
      commit('SET_CURRENT_FOLDER', getters.flatFoldersTree[getters.flatFoldersTree.length - 1])
    }
  },
  // common actions
  updateFoldersBreadcrumbs({ state, commit }, { folder }) {
    if (folder) {
      const breadcrumbIndex = findIndex(state.flatFoldersTree, item => item.id === folder.id)
      if (breadcrumbIndex === -1) {
        commit('ADD_FOLDER_TO_FLAT_LIST', folder)
      } else {
        commit('SPLICE_FLAT_LIST', breadcrumbIndex)
      }
    } else {
      commit('RESET_FLAT_LIST')
    }
  }
}
